import type { CatalogRef } from "@/lib/discount-evaluation/types"
import { centsToReais, formatBRL } from "@/lib/format-brl"

/**
 * What a coupon gives, read from its first rule (coupon-service `ruleSet`):
 * the delivery fee, the whole order, or catalog items. `items` keeps only the
 * item and category refs a customer can pick from the menu; `free` marks a
 * 100% rule ("sobremesa grátis"). The amount itself is never derived here:
 * the cart and checkout ask `coupons/evaluate`.
 */
export type CouponBenefit =
  | { kind: "delivery" }
  | { kind: "order" }
  | { kind: "items"; free: boolean; refs: CatalogRef[] }

type Json = Record<string, unknown>

function isObject(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function text(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function pickableRef(raw: unknown): CatalogRef | null {
  if (!isObject(raw)) return null
  const kind = text(raw.kind)
  if (kind !== "REF_KIND_ITEM" && kind !== "REF_KIND_CATEGORY") return null
  const ref: CatalogRef = {
    kind,
    id: text(raw.id),
    externalCode: text(raw.externalCode),
    name: text(raw.name),
    scopeItemId: text(raw.scopeItemId),
    scopeItemExternalCode: text(raw.scopeItemExternalCode),
  }
  return ref.id || ref.externalCode ? ref : null
}

/** The benefit of a coupon's `ruleSet` as the by-code read returns it; anything unreadable counts as an order discount. */
export function couponBenefitOf(ruleSet: unknown): CouponBenefit {
  const rules = isObject(ruleSet) && Array.isArray(ruleSet.rules) ? ruleSet.rules : []
  const first: unknown = rules[0]
  if (!isObject(first)) return { kind: "order" }
  const target = isObject(first.target) ? first.target : {}
  const value = isObject(first.value) ? first.value : {}
  const targetKind = text(target.kind)
  if (targetKind === "TARGET_KIND_DELIVERY_FEE") return { kind: "delivery" }
  if (targetKind !== "TARGET_KIND_ITEMS") return { kind: "order" }
  const refs = (Array.isArray(target.refs) ? target.refs : []).flatMap((raw) => pickableRef(raw) ?? [])
  if (refs.length === 0) return { kind: "order" }
  return { kind: "items", free: text(value.kind) === "VALUE_KIND_FREE", refs }
}

/**
 * How much a coupon's first rule gives, as coupon-service stores it: a share
 * (`basisPoints`, 1000 = 10%), an amount off, a fixed price, or free. Shown on
 * the bag only; the amount taken off always comes from `coupons/evaluate`.
 */
export type CouponReward =
  | { kind: "percent"; basisPoints: number }
  | { kind: "amount"; cents: number }
  | { kind: "price"; cents: number }
  | { kind: "free" }
  | { kind: "unknown" }

function wholeNumber(value: unknown): number {
  const parsed = typeof value === "string" ? Number(value) : value
  return typeof parsed === "number" && Number.isFinite(parsed) ? Math.round(parsed) : 0
}

/** The reward of a coupon's `ruleSet` as the by-code read returns it (int64 cents may arrive as strings); anything unreadable is `unknown`. */
export function couponRewardOf(ruleSet: unknown): CouponReward {
  const rules = isObject(ruleSet) && Array.isArray(ruleSet.rules) ? ruleSet.rules : []
  const first: unknown = rules[0]
  return rewardOfValue(isObject(first) && isObject(first.value) ? first.value : {})
}

function rewardOfValue(value: Json): CouponReward {
  const basisPoints = wholeNumber(value.percentBasisPoints)
  const cents = wholeNumber(value.amountCents)
  switch (text(value.kind)) {
    case "VALUE_KIND_FREE":
      return { kind: "free" }
    case "VALUE_KIND_PERCENT":
      if (basisPoints >= 10000) return { kind: "free" }
      return basisPoints > 0 ? { kind: "percent", basisPoints } : { kind: "unknown" }
    case "VALUE_KIND_FIXED_AMOUNT":
      return cents > 0 ? { kind: "amount", cents } : { kind: "unknown" }
    case "VALUE_KIND_FIXED_PRICE":
      return { kind: "price", cents }
    default:
      return { kind: "unknown" }
  }
}

/** What a coupon is for, the type the customer picks first: money off, free delivery or free items. */
export type CouponKind = "discount" | "free-shipping" | "free-items"

/** How a free-items coupon hands out its items: `one` is a total of `quantity` units among them, `each` is `quantity` units of every one. */
export type FreeItemsPick = "one" | "each"

/**
 * The simple, type-first shape of a coupon (coupon-service `offer`), present
 * only when the rules fit it: anything the offer cannot say ("Modo avançado")
 * is not an offer and reads from the rules instead.
 */
export type CouponOffer =
  | { kind: "discount"; reward: CouponReward }
  | { kind: "free-shipping" }
  | { kind: "free-items"; refs: CatalogRef[]; quantity: number; pick: FreeItemsPick }

const KIND_BY_NAME: Record<string, CouponKind> = {
  COUPON_KIND_DISCOUNT: "discount",
  COUPON_KIND_FREE_SHIPPING: "free-shipping",
  COUPON_KIND_FREE_ITEMS: "free-items",
}

/** The kind of a coupon record's `kind` field; null when absent (old deployments) or unknown. */
export function couponKindOf(raw: unknown): CouponKind | null {
  return typeof raw === "string" ? (KIND_BY_NAME[raw] ?? null) : null
}

/** The offer of a coupon record's `offer` field; null when absent, advanced or too incomplete to read. */
export function couponOfferOf(raw: unknown): CouponOffer | null {
  if (!isObject(raw) || raw.advanced === true) return null
  const kind = couponKindOf(raw.kind)
  if (kind === "free-shipping") return { kind }
  if (kind === "discount") {
    const discount = isObject(raw.discount) ? raw.discount : {}
    const reward = rewardOfValue({ ...discount, kind: discount.valueKind })
    return reward.kind === "percent" || reward.kind === "amount" ? { kind, reward } : null
  }
  if (kind !== "free-items") return null
  const freeItems = isObject(raw.freeItems) ? raw.freeItems : {}
  const refs = (Array.isArray(freeItems.items) ? freeItems.items : []).flatMap((item) => pickableRef(item) ?? [])
  if (refs.length === 0) return null
  const pick = text(freeItems.pick) === "FREE_ITEMS_PICK_EACH" && refs.length > 1 ? "each" : "one"
  return { kind, refs, quantity: Math.max(1, wholeNumber(freeItems.quantity)), pick }
}

/** The benefit and reward an offer stands for, in the shape the bag and the prize flow already read. */
export function couponOfferBenefit(offer: CouponOffer): { benefit: CouponBenefit; reward: CouponReward } {
  if (offer.kind === "discount") return { benefit: { kind: "order" }, reward: offer.reward }
  if (offer.kind === "free-shipping") return { benefit: { kind: "delivery" }, reward: { kind: "free" } }
  return { benefit: { kind: "items", free: true, refs: offer.refs }, reward: { kind: "free" } }
}

function namesOf(refs: readonly CatalogRef[]): string[] {
  return refs.map((ref) => ref.name.trim()).filter(Boolean)
}

function joined(names: readonly string[], last: string): string {
  if (names.length < 2) return names[0] ?? ""
  return `${names.slice(0, -1).join(", ")} ${last} ${names[names.length - 1]}`
}

function freeItemsWords(offer: Extract<CouponOffer, { kind: "free-items" }>): { headline: string; detail: string } {
  const names = namesOf(offer.refs)
  const { quantity } = offer
  if (offer.refs.length === 1) {
    const name = names[0]
    const headline = name
      ? quantity === 1 ? `1 ${name} grátis` : `${quantity}x ${name} grátis`
      : quantity === 1 ? "1 item grátis" : `${quantity} itens grátis`
    return { headline, detail: "no seu pedido" }
  }
  if (offer.pick === "one") {
    return {
      headline: quantity === 1 ? "1 item grátis" : `${quantity} itens grátis`,
      detail: names.length === offer.refs.length ? `Escolha entre ${joined(names, "ou")}` : "Escolha entre os itens do cupom",
    }
  }
  const all = names.length === offer.refs.length ? `${joined(names, "e")} grátis` : `${offer.refs.length * quantity} itens grátis`
  return { headline: all, detail: quantity === 1 ? "Você ganha todos" : `${quantity} de cada` }
}

function offerHeadline(offer: CouponOffer, maxDiscountCents: number): { headline: string; detail: string } | null {
  if (offer.kind === "free-shipping") return { headline: "Frete grátis", detail: "na entrega do seu pedido" }
  if (offer.kind === "free-items") return freeItemsWords(offer)
  const plain = plainHeadline({ kind: "order" }, offer.reward)
  if (!plain || offer.reward.kind !== "percent" || maxDiscountCents <= 0) return plain
  return { ...plain, headline: `${plain.headline} até ${shortCents(maxDiscountCents)}` }
}

function offerLabel(offer: CouponOffer): string | null {
  if (offer.kind === "free-shipping") return "Frete grátis"
  if (offer.kind === "discount") return couponBenefitLabel({ kind: "order" }, offer.reward)
  const { headline, detail } = freeItemsWords(offer)
  if (offer.refs.length > 1 && offer.pick === "one") return `${headline}: ${detail.replace(/^Escolha entre /, "")}`
  return headline
}

function percentLabel(basisPoints: number): string {
  return `${(basisPoints / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
}

function centsLabel(cents: number): string {
  return formatBRL(centsToReais(cents))
}

function itemsName(refs: readonly CatalogRef[]): string | null {
  const names = refs.map((ref) => ref.name.trim()).filter(Boolean)
  return names.length === 1 ? names[0] : null
}

/** What a coupon gives in a few words ("10% de desconto", "Frete grátis", "Pudim grátis"), the offer first when the record has one; null when the record says too little to tell. */
export function couponBenefitLabel(benefit: CouponBenefit, reward: CouponReward, offer: CouponOffer | null = null): string | null {
  if (offer) return offerLabel(offer)
  if (benefit.kind === "delivery") {
    if (reward.kind === "free") return "Frete grátis"
    if (reward.kind === "percent") return `${percentLabel(reward.basisPoints)} de desconto no frete`
    if (reward.kind === "amount") return `${centsLabel(reward.cents)} de desconto no frete`
    return "Desconto no frete"
  }
  if (benefit.kind === "order") {
    if (reward.kind === "percent") return `${percentLabel(reward.basisPoints)} de desconto`
    if (reward.kind === "amount") return `${centsLabel(reward.cents)} de desconto`
    return reward.kind === "free" ? "Pedido grátis" : null
  }
  const name = itemsName(benefit.refs)
  if (benefit.free || reward.kind === "free") return name ? `${name} grátis` : "1 item grátis"
  if (reward.kind === "percent") return `${percentLabel(reward.basisPoints)} de desconto${name ? ` em ${name}` : " em itens"}`
  if (reward.kind === "amount") return `${centsLabel(reward.cents)} de desconto${name ? ` em ${name}` : " em itens"}`
  if (reward.kind === "price") return `${name ?? "Item"} por ${centsLabel(reward.cents)}`
  return null
}

function shortCents(cents: number): string {
  return cents % 100 === 0 ? `R$ ${cents / 100}` : centsLabel(cents)
}

/** The benefit as the big line of a coupon ("10% OFF até R$ 15", "Frete grátis", "1 Pudim grátis"), the offer first when the record has one, and where it lands ("no seu pedido"); null when the record says too little. */
export function couponBenefitHeadline(
  benefit: CouponBenefit,
  reward: CouponReward,
  maxDiscountCents = 0,
  offer: CouponOffer | null = null,
): { headline: string; detail: string } | null {
  if (offer) return offerHeadline(offer, maxDiscountCents)
  const plain = plainHeadline(benefit, reward)
  if (!plain || reward.kind !== "percent" || maxDiscountCents <= 0) return plain
  return { ...plain, headline: `${plain.headline} até ${shortCents(maxDiscountCents)}` }
}

function plainHeadline(benefit: CouponBenefit, reward: CouponReward): { headline: string; detail: string } | null {
  if (benefit.kind === "delivery") {
    if (reward.kind === "free") return { headline: "Frete grátis", detail: "na entrega do seu pedido" }
    if (reward.kind === "percent") return { headline: `${percentLabel(reward.basisPoints)} OFF`, detail: "na taxa de entrega" }
    if (reward.kind === "amount") return { headline: `${shortCents(reward.cents)} OFF`, detail: "na taxa de entrega" }
    return { headline: "Desconto", detail: "na taxa de entrega" }
  }
  if (benefit.kind === "order") {
    if (reward.kind === "percent") return { headline: `${percentLabel(reward.basisPoints)} OFF`, detail: "no seu pedido" }
    if (reward.kind === "amount") return { headline: `${shortCents(reward.cents)} OFF`, detail: "no seu pedido" }
    return null
  }
  const name = itemsName(benefit.refs)
  const where = name ? `em ${name}` : "em itens selecionados"
  if (benefit.free || reward.kind === "free") return { headline: name ? `${name} grátis` : "Item grátis", detail: "no seu pedido" }
  if (reward.kind === "percent") return { headline: `${percentLabel(reward.basisPoints)} OFF`, detail: where }
  if (reward.kind === "amount") return { headline: `${shortCents(reward.cents)} OFF`, detail: where }
  if (reward.kind === "price") return { headline: centsLabel(reward.cents), detail: name ? `${name} por esse preço` : "no item do cupom" }
  return null
}

function hasContent(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  if (isObject(value)) return Object.values(value).some(hasContent)
  return value !== undefined && value !== null && value !== "" && value !== false && value !== 0
}

/** How plain a coupon's rule set is: `conditional` when it has several rules or any condition, and the per-order cap in cents (0 = none). */
export function couponRuleShapeOf(ruleSet: unknown): { conditional: boolean; maxDiscountCents: number } {
  const rules = isObject(ruleSet) && Array.isArray(ruleSet.rules) ? ruleSet.rules : []
  const first: unknown = rules[0]
  if (rules.length !== 1 || !isObject(first)) return { conditional: true, maxDiscountCents: 0 }
  return { conditional: hasContent(first.conditions), maxDiscountCents: wholeNumber(first.maxDiscountCents) }
}
